import { ftCampaignNowSeconds, ftClockCalendarFromArr, ftClockCampaignField, ftClockColsFromArr } from '../../shared/campaign-clock';
import type { StealthLockoutState } from '../actions/ability-preconditions';

type Row = Record<string, unknown>;
const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const calendar = (campaign: unknown): Row | null => record(record(record(record(campaign)?.computed)?.campaign)?.calendar);

/** A held calendar is reusable only with the persisted campaign identity and revision that supplied it. */
export function stealthLockoutCalendarRef(campaign: unknown): Record<string, string> | null {
    if (!Array.isArray(calendar(campaign)?.months)) return null;
    const ref = Object.fromEntries(['campaign_id', 'calendar_id', 'campaign_t_updated']
        .map(key => [key, String(ftClockCampaignField(campaign, key) ?? '')]));
    return Object.values(ref).every(value => value !== '') ? ref : null;
}

/** Resolve only the missing calendar, never replace/refetch an encounter or trust transport verdicts. */
export function stealthLockoutCampaign(facts: Row, heldCampaign?: unknown): Row | null {
    const campaign = record(facts.campaign_arr);
    if (!campaign || !ftClockCampaignField(campaign, 'campaign_id')) return null;
    if (Array.isArray(calendar(campaign)?.months)) return campaign;
    const heldRef = stealthLockoutCalendarRef(heldCampaign);
    if (!heldRef || !Object.entries(heldRef).every(([key, value]) => String(ftClockCampaignField(campaign, key) ?? '') === value)) return null;
    return { campaigns: campaign.campaigns, computed: { campaign: { calendar: calendar(heldCampaign) } } };
}

/** Headless Hide lockout decision. Missing hydration is unknown, never permission to Hide.
 * Zero/negative deadlines are real; equality expires the lockout. Omitted lean clock columns read as zero. */
export function stealthLockoutDecision(facts: unknown, heldCampaign?: unknown): StealthLockoutState | null {
    const raw = record(facts);
    if (!raw || raw.error || typeof raw.campaign !== 'boolean') return null;
    if (!raw.campaign) return { campaign: false, active: false, minutes: 0 };
    const campaign = stealthLockoutCampaign(raw, heldCampaign);
    if (!campaign) return null;
    const until = raw.until == null ? null : Number(raw.until);
    if (until !== null && !Number.isFinite(until)) return null;
    const now = ftCampaignNowSeconds(ftClockColsFromArr(campaign), ftClockCalendarFromArr(campaign));
    const active = until !== null && now < until;
    return { campaign: true, active, minutes: active ? Math.ceil((until - now) / 60) : 0 };
}
